import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Zap, Loader2, X, Check, Sparkles } from "lucide-react";
import { apiClient, formatApiError } from "../../lib/auth";

export default function TopUpModal({ open, onClose }) {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true); setError("");
    apiClient.get("/credits/topups")
      .then(({ data }) => setTiers(data.tiers || []))
      .catch((e) => setError(formatApiError(e, "Couldn't load top-up options.")))
      .finally(() => setLoading(false));
  }, [open]);

  const buy = async (tierId) => {
    setBuyingId(tierId); setError("");
    try {
      const { data } = await apiClient.post("/credits/topup/checkout", { tier_id: tierId, origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) { setError(formatApiError(e, "Couldn't start checkout. Try again.")); setBuyingId(null); }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="topup-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-md" onClick={onClose} />
      <div className="relative bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-[0_30px_80px_-20px_rgba(15,23,42,0.35)] overflow-hidden">
        <button data-testid="topup-close" onClick={onClose} className="absolute top-5 right-5 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="w-11 h-11 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-3"><Zap className="w-5 h-5 text-emerald-600" /></div>
          <h3 className="font-display text-xl font-bold text-slate-900">Top up your credits</h3>
          <p className="text-sm text-slate-500 mt-1">Instant credits for calling & texting. They never expire and stack on top of your monthly allowance.</p>
        </div>

        <div className="px-6 py-5 space-y-2.5">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading options…</div>
          ) : (
            tiers.map((t) => (
              <button key={t.id} data-testid={`topup-tier-${t.id}`} onClick={() => buy(t.id)} disabled={!!buyingId}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-2xl border text-left transition-all disabled:opacity-60 ${t.best_value ? "border-emerald-400 bg-emerald-50/60 hover:bg-emerald-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${t.best_value ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600"}`}>
                    {t.best_value ? <Sparkles className="w-5 h-5" /> : <Zap className="w-5 h-5" />}
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 text-sm flex items-center gap-2">
                      {t.credits.toLocaleString()} credits
                      {t.best_value && <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">Best value</span>}
                    </div>
                    <div className="text-xs text-slate-500">{t.label}</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {buyingId === t.id ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : <span className="font-display text-lg font-bold text-slate-900">${t.price_usd.toFixed(0)}</span>}
                </div>
              </button>
            ))
          )}
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="topup-error">{error}</div>}
        </div>

        <div className="px-6 py-3.5 border-t border-slate-100 flex items-center gap-2 text-[11px] text-slate-400">
          <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> Secure one-time payment via Stripe. Credits are added instantly.
        </div>
      </div>
    </div>,
    document.body
  );
}
